import { ChartType, authentication } from '@ecosystem/newron-client-lib'
import { InsightInfo } from '@ecosystem/newron-client-collections'
import { stringToNumericalHashCode } from 'util/formatting'
import {
  isValidContext,
  filterContext,
  generateCombinationSet,
  mapFiltersWithValues,
} from 'util/validateContext'
import { getApolloClientWithAuthentication } from 'query/client'
import { Variables } from 'reducers/filtering/selectors'
import { GET_ALL_FILTERS, GET_FILTER_VALUES, GET_USER_SUBSCRIPTION } from 'query/query'
import { Schema, CategoryGroup, Variable, FilterData, Answer, Filter } from 'typings/filters'
import { InsightData } from 'containers/charts'
import { isAllowedCiePath } from 'util/urlUtils'
import { TRACKING } from 'util/analytics'
import { Cell } from 'reducers/subscription/user'
import { InsightTag } from '@ecosystem/newron-client-collections/build/shared/types'
import { decompressData } from 'containers/LoadFilters'

export interface CollectionsRegister {
  collectionInsight: any
  collectionKey: string
  chartType: ChartType
  schema?: string
  chartName: string
}

interface GetFilterTagsProps {
  schema: Schema
  insight: InsightData
  context?: any
}
interface GetFilterValues {
  schema: Schema
  context: Cell[]
  filters: Omit<FilterData, 'answer_id'>[]
  variableId: number
}
interface ContextItem {
  __typename: string
  [key: string]: any
}

export async function setUserContextToSessionStorage() {
  const user = await authentication.getUser()
  let context
  if (user) {
    const hash = stringToNumericalHashCode(user.email).toString()
    const hashFromSessionStorage = sessionStorage.getItem('consumer_user_hash')

    if (hashFromSessionStorage && hashFromSessionStorage !== hash) {
      sessionStorage.removeItem('consumer_context_' + hashFromSessionStorage)
      sessionStorage.removeItem('consumer_user_hash')
    }

    const contextFromSessionStorage = sessionStorage.getItem('consumer_context_' + hash)
    context = contextFromSessionStorage ? JSON.parse(contextFromSessionStorage) : null
    if (!contextFromSessionStorage || user.isInternalUser) {
      const apolloClient = getApolloClientWithAuthentication(await authentication.getAccessToken())
      const result = await apolloClient.query<{ context: any }>({
        query: GET_USER_SUBSCRIPTION,
      })
      // Remove __typename from the result
      const contextWithoutTypename = result?.data?.context.map(
        ({ __typename, ...rest }: ContextItem) => rest
      )
      context = contextWithoutTypename
      sessionStorage.setItem('consumer_user_hash', hash)
      sessionStorage.setItem('consumer_context_' + hash, JSON.stringify(contextWithoutTypename))
    }
  }
  return context
}
export function isSelectControlEnabled(selectItem?: string, hasSelectedControl?: boolean) {
  return selectItem && hasSelectedControl
}
function isSelectDonutViewByEnabled(
  selectItem?: string,
  graphqlVars?: any,
  selectedDonutViewBy?: any
) {
  const showDonutViewBy =
    selectItem && graphqlVars?.selectedDonutViewBy && !graphqlVars.hasAudienceControl
  return showDonutViewBy ? [{ key: 'View by', value: selectedDonutViewBy.label }] : []
}

export function getKBQViewBys(insight: InsightData): { key: string; value: string }[] | undefined {
  const { graphqlVars } = insight || {}
  const {
    kbqSelectAudience,
    kbqSelectItem,
    selectCompetitorsLabels,
    kbqSelectChannel,
    selectedDonutViewBy,
  } = graphqlVars || {}
  const showSelectBrand = isSelectControlEnabled(kbqSelectItem, graphqlVars?.hasSelectBrand)
  const showSelectRetail = isSelectControlEnabled(kbqSelectItem, graphqlVars?.hasSelectRetailer)
  const showSelectChannel = isSelectControlEnabled(kbqSelectChannel, graphqlVars?.hasSelectRetailer)
  const showAudienceControl = isSelectControlEnabled(
    kbqSelectAudience,
    graphqlVars?.hasAudienceControl
  )
  const donutViewBy = isSelectDonutViewByEnabled(
    kbqSelectAudience,
    graphqlVars,
    selectedDonutViewBy
  )

  if (kbqSelectAudience || kbqSelectItem) {
    const viewBys = [
      ...(showSelectBrand ? [{ key: 'Select brand', value: kbqSelectItem }] : []),
      ...(showSelectRetail ? [{ key: 'Select retailer', value: kbqSelectItem }] : []),
      ...(showSelectChannel ? [{ key: 'Select channel visited', value: kbqSelectChannel }] : []),
      ...(showAudienceControl
        ? [{ key: 'Select shoppers for comparison', value: kbqSelectAudience }]
        : []),
      ...donutViewBy,
      ...(selectCompetitorsLabels?.length
        ? [{ key: 'Select competitors', value: selectCompetitorsLabels.join(',') }]
        : []),
    ]
    return viewBys.sort((a, b) => a.key.localeCompare(b.key))
  }
}

export function getSelectedCurrencyLabel(insight: InsightData): string | undefined {
  return insight?.currencyOptions?.find((vars) => vars.value === insight.graphqlVars.secondaryVar)
    ?.label
}

function buildTagsArray(
  insight: InsightData,
  filterMap: Map<number, Variable>
): InsightInfo['tags'] {
  return (insight?.graphqlVars?.filters as FilterData[]).map((f) => {
    const filter = filterMap.get(f.variable_id)

    return {
      key: filter?.label ?? '',
      value: filter?.values.find((c) => c.answer_code === f.answer_code)?.label ?? '',
    }
  })
}
export function getViewByLabel(insight: InsightData): string | undefined {
  const getLabel = (varName: string) => {
    if (insight?.graphqlVars[varName]) {
      return insight.viewByOptions.filter((vars) => vars.value === insight.graphqlVars[varName])[0]
        ?.label
    }
  }

  return getLabel('secondaryVar') || getLabel('tertiaryVar') || getLabel('primaryVar')
}
export function buildFinalTags(
  insight: InsightData,
  tags: InsightInfo['tags']
): InsightInfo['tags'] {
  const selectedViewBy = insight?.viewByOptions?.length && getViewByLabel(insight)
  const kbqViewBys = insight?.graphqlVars?.kbqSelectItem && getKBQViewBys(insight)

  const selectedCurrencyLabel = getSelectedCurrencyLabel(insight)

  const tagsWithCurrency = selectedCurrencyLabel
    ? [{ key: 'Currency', value: selectedCurrencyLabel }, ...tags]
    : tags

  if (selectedViewBy) {
    const tagsWithCurrencyorViewBy = selectedCurrencyLabel
      ? [
          { key: 'Currency', value: selectedCurrencyLabel },
          { key: 'ViewBy', value: selectedViewBy },
          ...tags,
        ]
      : [{ key: 'ViewBy', value: selectedViewBy }, ...tags]
    return tagsWithCurrencyorViewBy
  } else if (kbqViewBys?.length) {
    return [...kbqViewBys, ...tagsWithCurrency]
  } else {
    return tagsWithCurrency
  }
}

export function filterEmptyTags(tags: InsightInfo['tags']): InsightInfo['tags'] {
  return tags?.filter((t) => t.key !== '' && t.value !== '') || []
}
export async function fetchFilterValues({
  schema,
  context,
  variableId,
  filters,
}: GetFilterValues): Promise<(Answer | Filter)[]> {
  const apolloClient = getApolloClientWithAuthentication(await authentication.getAccessToken())
  const queryVars = {
    schema,
    context,
    variableId,
    filters,
  }
  const result = await apolloClient.query<{ filterValues: (Answer | Filter)[] }>({
    query: GET_FILTER_VALUES,
    fetchPolicy: 'cache-first',
    variables: queryVars,
  })
  return result?.data?.filterValues
}

export async function fetchDataFromApollo({
  schema,
  context,
}: Omit<GetFilterTagsProps, 'insight'>): Promise<CategoryGroup[]> {
  const apolloClient = getApolloClientWithAuthentication(await authentication.getAccessToken())
  const queryVars = {
    schema,
    context,
  }
  const result = await apolloClient.query<{ filters: string }>({
    query: GET_ALL_FILTERS,
    fetchPolicy: 'cache-first',
    variables: queryVars,
  })
  const decompressedData = decompressData(result.data.filters)

  return decompressedData?.filters as CategoryGroup[]
}

export async function fetchAndProcessDataFromApollo({
  schema,
  context,
}: Omit<GetFilterTagsProps, 'insight'>): Promise<Map<number, Variable>> {
  const filterData = await fetchDataFromApollo({ schema, context })
  const filterValuesMap: Map<number, Variable> = new Map()

  const filtersMap = filterData?.reduce((map, obj) => {
    obj.categories.forEach((category) => {
      category.variables.forEach((variable) => {
        map.set(variable.variable_id, variable)
      })
    })
    return map
  }, filterValuesMap)

  return filtersMap
}

export async function processFiltersDataAndContext({
  schema,
  insight,
  context: loggedInUserContext,
}: GetFilterTagsProps & { filterData: Map<number, Variable> }) {
  const filterData = await fetchAndProcessDataFromApollo({ schema, context: loggedInUserContext })
  const getValueData = (variableId: number) => filterData.get(variableId)?.values ?? []
  const prodValuesData = getValueData(Variables.PRODUCT_GROUP)
  const countryValuesData = getValueData(Variables.COUNTRY)

  const productFilters = mapFiltersWithValues(
    insight.graphqlVars.filters,
    Variables.PRODUCT_GROUP,
    prodValuesData
  )
  const marketFilters = mapFiltersWithValues(
    insight.graphqlVars.filters,
    Variables.COUNTRY,
    countryValuesData
  )
  insight.graphqlVars.productFilters = productFilters
  insight.graphqlVars.marketFilters = marketFilters

  const combinationSet = generateCombinationSet(productFilters, marketFilters)
  const filteredSharedContext = filterContext(insight.graphqlVars.context, combinationSet)
  insight.graphqlVars.validContext = isValidContext(
    filteredSharedContext as Omit<Cell, 'waves'>[],
    loggedInUserContext
  )
}
export async function getFilterTags({
  schema,
  insight,
}: GetFilterTagsProps): Promise<InsightInfo['tags']> {
  const loggedInUserContext = await setUserContextToSessionStorage()
  insight.graphqlVars.userContext = loggedInUserContext
  const filterData = await fetchAndProcessDataFromApollo({ schema, context: loggedInUserContext })
  await processFiltersDataAndContext({ schema, insight, context: loggedInUserContext, filterData })

  const tags = buildTagsArray(insight, filterData)
  const groupedData = tags.reduce(
    (acc, item) => {
      acc[item.key] = acc[item.key] || []
      acc[item.key].push(item)
      return acc
    },
    {} as Record<string, InsightTag[]>
  )

  const keys = Object.keys(groupedData).sort((a: any, b: any) => {
    if (a === b) {
      return groupedData[a][0].value.localeCompare(groupedData[b][0].value)
    } else {
      return a.localeCompare(b)
    }
  })

  const sortedTags = keys
    .map((key) => {
      return groupedData[key].sort((a, b) => a.value.localeCompare(b.value))
    })
    .flat()

  const finalTags = buildFinalTags(insight, sortedTags)

  return filterEmptyTags(finalTags)
}

export async function getInsightInfo(insight: InsightData, collection: CollectionsRegister) {
  return {
    tags: !isAllowedCiePath(window.location.pathname)
      ? await getFilterTags({
          schema: collection.schema ? Schema.CIE_REVIEW : Schema.CIE,
          insight,
        })
      : [],
    trackingTags: {
      access: TRACKING.ACCESS,
      chartType: collection.chartType,
    },
    chartName: collection.chartName,
  }
}
